import { PLAElementRecord, PLARelationRecord, PLAProject, AttributeValue, PLAElementTypeDefinition } from "./PLAState";

type TokenType = "number" | "identifier" | "attribute" | "operator" | "paren" | "comma" | "dot" | "comparison";
type Token = { type: TokenType; value: string };
type AttributeReference = { type: "literal"; name: string } | { type: "localAttribute"; name: string };

type ExpressionNode =
   | { type: "number"; value: number }
   | { type: "attribute"; name: string }
   | { type: "binary"; operator: string; left: ExpressionNode; right: ExpressionNode }
   | { type: "function"; name: string; args: ExpressionNode[] }
   | { type: "traversal"; aggregator: string; relationType: string; attribute: AttributeReference; deep?: boolean; functionName?: string }
   | { type: "parentLookup"; relationType: string; attribute: AttributeReference }
   | { type: "parentTraversal"; aggregator: string; parentRelationType: string; relationType: string; attribute: AttributeReference };

export type PLAEvaluationContext = {
   element: PLAElementRecord;
   elements: PLAElementRecord[];
   relations: PLARelationRecord[];
   project?: PLAProject;
   calculated?: Record<string, any>;
   resolveCalculatedAttribute?: (element: PLAElementRecord, attributeId: string) => any;
};

export type PLAExpressionValidationContext = {
   project?: PLAProject;
   elementTypeId?: string;
   elementType?: PLAElementTypeDefinition;
};

export type PLAExpressionExplanationStep = {
   label: string;
   value: any;
   valueType: string;
   details?: string[];
   children?: PLAExpressionExplanationStep[];
};

export type PLAExpressionExplanation = {
   expression: string;
   result: any;
   valueType: string;
   root: PLAExpressionExplanationStep;
   lines: string[];
};

const aggregateFunctions = ["sum", "avg", "min", "max", "count"];
const traversalFunctions = [...aggregateFunctions, "single"];
const deepTraversalFunctions: Record<string, string> = {
   deepSum: "sum",
   deepAvg: "avg",
   deepMin: "min",
   deepMax: "max",
   deepCount: "count",
   deepSingle: "single"
};
const parentTraversalFunctions: Record<string, string> = {
   parentSum: "sum",
   parentAvg: "avg",
   parentMin: "min",
   parentMax: "max",
   parentCount: "count",
   parentSingle: "single"
};
const parentLookupFunction = "parent";

function readEscapedText(expression: string, start: number, endChar: string): { value: string; end: number } {
   let value = "";
   let index = start;
   while (index < expression.length) {
      const char = expression[index];
      if (char === "\\") {
         if (index + 1 >= expression.length) throw new Error("Invalid escape sequence");
         value += expression[index + 1];
         index += 2;
         continue;
      }
      if (char === endChar) return { value, end: index };
      value += char;
      index++;
   }
   throw new Error(`Missing closing ${endChar}`);
}

function readQuotedIdentifier(expression: string, start: number): { value: string; end: number } {
   const quote = expression[start];
   const result = readEscapedText(expression, start + 1, quote);
   if (!result.value.trim()) throw new Error("Empty quoted name");
   return { value: result.value, end: result.end + 1 };
}

function readIdentifier(expression: string, start: number): { value: string; end: number } | undefined {
   let value = "";
   let index = start;
   while (index < expression.length) {
      const char = expression[index];
      if (char === "\\") {
         if (index + 1 >= expression.length) throw new Error("Invalid escape sequence");
         value += expression[index + 1];
         index += 2;
         continue;
      }
      if (/[A-Za-z0-9_ÆØÅæøå-]/.test(char)) {
         value += char;
         index++;
         continue;
      }
      break;
   }
   if (!value || !/^[A-Za-z_ÆØÅæøå]/.test(value)) return undefined;
   return { value, end: index };
}

export function tokenizePLAExpression(expression: string): Token[] {
   const tokens: Token[] = [];
   let index = 0;

   while (index < expression.length) {
      const char = expression[index];
      if (/\s/.test(char)) {
         index++;
         continue;
      }
      if (char === "[") {
         const result = readEscapedText(expression, index + 1, "]");
         const name = result.value.trim();
         if (!name) throw new Error("Empty attribute reference");
         tokens.push({ type: "attribute", value: name });
         index = result.end + 1;
         continue;
      }
      if (char === '"' || char === "'") {
         const identifier = readQuotedIdentifier(expression, index);
         tokens.push({ type: "identifier", value: identifier.value });
         index = identifier.end;
         continue;
      }
      const comparison = expression.slice(index).match(/^(>=|<=|==|!=|>|<)/);
      if (comparison) {
         tokens.push({ type: "comparison", value: comparison[1] });
         index += comparison[1].length;
         continue;
      }
      if (/[+\-*/]/.test(char)) {
         tokens.push({ type: "operator", value: char });
         index++;
         continue;
      }
      if (char === "(" || char === ")") {
         tokens.push({ type: "paren", value: char });
         index++;
         continue;
      }
      if (char === ",") {
         tokens.push({ type: "comma", value: char });
         index++;
         continue;
      }
      if (char === ".") {
         tokens.push({ type: "dot", value: char });
         index++;
         continue;
      }
      const numberMatch = expression.slice(index).match(/^\d+(?:\.\d+)?/);
      if (numberMatch) {
         tokens.push({ type: "number", value: numberMatch[0] });
         index += numberMatch[0].length;
         continue;
      }
      const identifier = readIdentifier(expression, index);
      if (identifier) {
         tokens.push({ type: "identifier", value: identifier.value });
         index = identifier.end;
         continue;
      }
      throw new Error(`Unexpected character '${char}'`);
   }

   return tokens;
}

class PLAExpressionParser {
   private position = 0;

   constructor(private readonly tokens: Token[]) { }

   parse(): ExpressionNode {
      const node = this.parseComparison();
      if (this.peek()) throw new Error(`Unexpected token '${this.peek()?.value}'`);
      return node;
   }

   private parseComparison(): ExpressionNode {
      let node = this.parseAdditive();
      while (this.peek()?.type === "comparison") {
         const operator = this.consume().value;
         node = { type: "binary", operator, left: node, right: this.parseAdditive() };
      }
      return node;
   }

   private parseAdditive(): ExpressionNode {
      let node = this.parseMultiplicative();
      while (this.peek()?.type === "operator" && ["+", "-"].includes(this.peek()!.value)) {
         const operator = this.consume().value;
         node = { type: "binary", operator, left: node, right: this.parseMultiplicative() };
      }
      return node;
   }

   private parseMultiplicative(): ExpressionNode {
      let node = this.parseUnary();
      while (this.peek()?.type === "operator" && ["*", "/"].includes(this.peek()!.value)) {
         const operator = this.consume().value;
         node = { type: "binary", operator, left: node, right: this.parseUnary() };
      }
      return node;
   }

   private parseUnary(): ExpressionNode {
      if (this.peek()?.type === "operator" && this.peek()?.value === "-") {
         this.consume();
         return { type: "binary", operator: "*", left: { type: "number", value: -1 }, right: this.parseUnary() };
      }
      return this.parsePrimary();
   }

   private parsePrimary(): ExpressionNode {
      const token = this.peek();
      if (!token) throw new Error("Unexpected end of expression");
      if (token.type === "number") {
         this.consume();
         return { type: "number", value: Number(token.value) };
      }
      if (token.type === "attribute") {
         this.consume();
         return { type: "attribute", name: token.value };
      }
      if (token.type === "paren" && token.value === "(") {
         this.consume();
         const node = this.parseComparison();
         this.expect("paren", ")");
         return node;
      }
      if (token.type === "identifier") {
         return this.parseFunctionOrTraversal();
      }
      throw new Error(`Unexpected token '${token.value}'`);
   }

   private parseFunctionOrTraversal(): ExpressionNode {
      const functionName = this.expect("identifier").value;
      this.expect("paren", "(");
      if (parentTraversalFunctions[functionName] && this.peek()?.type === "identifier" && this.tokens[this.position + 1]?.type === "dot" && this.tokens[this.position + 3]?.type === "dot") {
         const parentRelationType = this.expect("identifier").value;
         this.expect("dot", ".");
         const relationType = this.expect("identifier").value;
         this.expect("dot", ".");
         const attribute = this.parseAttributeReference();
         this.expect("paren", ")");
         return { type: "parentTraversal", aggregator: parentTraversalFunctions[functionName], parentRelationType, relationType, attribute };
      }
      if ((traversalFunctions.includes(functionName) || deepTraversalFunctions[functionName] || functionName === parentLookupFunction) && this.peek()?.type === "identifier" && this.tokens[this.position + 1]?.type === "dot") {
         const relationType = this.expect("identifier").value;
         this.expect("dot", ".");
         const attribute = this.parseAttributeReference();
         this.expect("paren", ")");
         if (functionName === parentLookupFunction) return { type: "parentLookup", relationType, attribute };
         return { type: "traversal", aggregator: deepTraversalFunctions[functionName] || functionName, relationType, attribute, deep: Boolean(deepTraversalFunctions[functionName]), functionName };
      }
      const args: ExpressionNode[] = [];
      if (!(this.peek()?.type === "paren" && this.peek()?.value === ")")) {
         do {
            args.push(this.parseComparison());
            if (this.peek()?.type !== "comma") break;
            this.consume();
         } while (true);
      }
      this.expect("paren", ")");
      return { type: "function", name: functionName, args };
   }

   private parseAttributeReference(): AttributeReference {
      const token = this.peek();
      if (token?.type === "identifier") return { type: "literal", name: this.consume().value };
      if (token?.type === "attribute") return { type: "localAttribute", name: this.consume().value };
      throw new Error("Expected attribute name");
   }

   private peek(): Token | undefined {
      return this.tokens[this.position];
   }

   private consume(): Token {
      return this.tokens[this.position++];
   }

   private expect(type: TokenType, value?: string): Token {
      const token = this.consume();
      if (!token || token.type !== type || (value !== undefined && token.value !== value)) {
         throw new Error(`Expected ${value ?? type}`);
      }
      return token;
   }
}

export function parsePLAExpression(expression: string): ExpressionNode {
   return new PLAExpressionParser(tokenizePLAExpression(expression)).parse();
}

function findAttributeDefinitionId(reference: string, definitions: Record<string, { name: string }>): string | undefined {
   return Object.entries(definitions).find(([attributeId, definition]) => attributeId === reference || definition.name === reference)?.[0];
}

function findRelationTypeDefinitionId(reference: string, project: PLAProject): string | undefined {
   if (reference === "outgoing" || reference === "incoming") return reference;
   return Object.entries(project.relationTypes || {}).find(([relationTypeId, relationType]) => relationTypeId === reference || relationType.name === reference)?.[0];
}

function collectAttributeReference(attribute: AttributeReference, references: { attributes: string[]; relationTypes: string[]; traversals: { relationType: string; attribute: AttributeReference }[] }) {
   if (attribute.type === "localAttribute") references.attributes.push(attribute.name);
}

function collectExpressionReferences(node: ExpressionNode, references: { attributes: string[]; relationTypes: string[]; traversals: { relationType: string; attribute: AttributeReference }[] }) {
   switch (node.type) {
      case "attribute":
         references.attributes.push(node.name);
         return;
      case "binary":
         collectExpressionReferences(node.left, references);
         collectExpressionReferences(node.right, references);
         return;
      case "function":
         node.args.forEach((arg) => collectExpressionReferences(arg, references));
         return;
      case "traversal":
         collectAttributeReference(node.attribute, references);
         references.traversals.push({ relationType: node.relationType, attribute: node.attribute });
         return;
      case "parentLookup":
         collectAttributeReference(node.attribute, references);
         references.traversals.push({ relationType: node.relationType, attribute: node.attribute });
         return;
      case "parentTraversal":
         references.relationTypes.push(node.parentRelationType);
         collectAttributeReference(node.attribute, references);
         references.traversals.push({ relationType: node.relationType, attribute: node.attribute });
         return;
      default:
         return;
   }
}

function validateExpressionReferences(node: ExpressionNode, context: PLAExpressionValidationContext): string | undefined {
   const project = context.project;
   if (!project) return undefined;
   const references = { attributes: [] as string[], relationTypes: [] as string[], traversals: [] as { relationType: string; attribute: AttributeReference }[] };
   collectExpressionReferences(node, references);

   const elementType = context.elementType || (context.elementTypeId ? project.elementTypes?.[context.elementTypeId] : undefined);
   if (elementType) {
      for (const attributeName of references.attributes) {
         if (!findAttributeDefinitionId(attributeName, elementType.attributes || {})) return `Unknown attribute: ${attributeName}`;
      }
   }

   for (const relationType of references.relationTypes) {
      if (!findRelationTypeDefinitionId(relationType, project)) return `Unknown relation type: ${relationType}`;
   }

   for (const traversal of references.traversals) {
      if (!findRelationTypeDefinitionId(traversal.relationType, project)) return `Unknown relation type: ${traversal.relationType}`;
      if (traversal.attribute.type === "localAttribute") continue;
      const searchableElementTypes = [context.elementType, ...Object.values(project.elementTypes || {})].filter(Boolean) as PLAElementTypeDefinition[];
      const attributeExists = searchableElementTypes.some((type) => Boolean(findAttributeDefinitionId(traversal.attribute.name, type.attributes || {})));
      if (!attributeExists) return `Unknown traversal attribute: ${traversal.attribute.name}`;
   }
   return undefined;
}

export function validatePLAExpression(expression: string, context: PLAExpressionValidationContext = {}): { valid: boolean; error?: string } {
   try {
      const node = parsePLAExpression(expression);
      const referenceError = validateExpressionReferences(node, context);
      if (referenceError) return { valid: false, error: referenceError };
      return { valid: true };
   } catch (error: any) {
      return { valid: false, error: error.message || String(error) };
   }
}

function numeric(value: any): number {
   if (value === true) return 1;
   if (value === false || value === undefined || value === null || value === "") return 0;
   const numberValue = Number(value);
   return Number.isFinite(numberValue) ? numberValue : 0;
}

function resolveAttributeValue(attribute: AttributeValue | undefined, calculated: Record<string, any>, name: string): any {
   if (Object.prototype.hasOwnProperty.call(calculated, name)) return calculated[name];
   if (!attribute) return undefined;
   if (attribute.mode === "constant") return attribute.value;
   return undefined;
}

function findAttributeIdByReference(reference: string, context: PLAEvaluationContext, element: PLAElementRecord = context.element): string {
   if (Object.prototype.hasOwnProperty.call(element.attributes, reference)) return reference;
   const definitions = context.project?.elementTypes[element.elementType]?.attributes || {};
   return Object.entries(definitions).find(([attributeId, definition]) => attributeId === reference || definition.name === reference)?.[0] || reference;
}

function findRelationTypeIdByReference(reference: string, context: PLAEvaluationContext): string {
   if (reference === "outgoing" || reference === "incoming") return reference;
   const relationTypes = context.project?.relationTypes || {};
   return Object.entries(relationTypes).find(([relationTypeId, relationType]) => relationTypeId === reference || relationType.name === reference)?.[0] || reference;
}

function resolveAttributeReference(reference: string, context: PLAEvaluationContext): any {
   const attributeId = findAttributeIdByReference(reference, context);
   const definition = context.project?.elementTypes[context.element.elementType]?.attributes?.[attributeId];
   if (definition?.type === "expression") {
      if (Object.prototype.hasOwnProperty.call(context.calculated || {}, attributeId)) return context.calculated?.[attributeId];
      if (context.resolveCalculatedAttribute) return context.resolveCalculatedAttribute(context.element, attributeId);
      if (context.project) return resolvePLACalculatedAttributes(context.element, context.project, context.elements, context.relations)[attributeId];
   }
   return resolveAttributeValue(context.element.attributes[attributeId], context.calculated || {}, attributeId);
}

function compare(operator: string, left: any, right: any) {
   switch (operator) {
      case ">": return numeric(left) > numeric(right);
      case "<": return numeric(left) < numeric(right);
      case ">=": return numeric(left) >= numeric(right);
      case "<=": return numeric(left) <= numeric(right);
      case "==": return left == right;
      case "!=": return left != right;
      default: throw new Error(`Unsupported operator '${operator}'`);
   }
}

function valueType(value: any): string {
   if (value === null) return "null";
   if (value === undefined) return "undefined";
   if (Array.isArray(value)) return "array";
   return typeof value;
}

function formatValue(value: any): string {
   if (typeof value === "string") return `"${value}"`;
   if (value === undefined) return "undefined";
   return String(value);
}

function elementLabel(element: PLAElementRecord): string {
   return `${element.name || element.id} (${element.id})`;
}

function attributeReferenceLabel(attribute: AttributeReference): string {
   return attribute.type === "localAttribute" ? `[${attribute.name}]` : attribute.name;
}

function nodeLabel(node: ExpressionNode): string {
   switch (node.type) {
      case "number": return String(node.value);
      case "attribute": return `[${node.name}]`;
      case "binary": return `${nodeLabel(node.left)} ${node.operator} ${nodeLabel(node.right)}`;
      case "function": return `${node.name}(${node.args.map(nodeLabel).join(", ")})`;
      case "traversal": return `${node.functionName || node.aggregator}(${node.relationType}.${attributeReferenceLabel(node.attribute)})`;
      case "parentLookup": return `parent(${node.relationType}.${attributeReferenceLabel(node.attribute)})`;
      case "parentTraversal": return `parent${node.aggregator}(${node.parentRelationType}.${node.relationType}.${attributeReferenceLabel(node.attribute)})`;
      default: return "expression";
   }
}

function explanationStep(label: string, value: any, details?: string[], children?: PLAExpressionExplanationStep[]): PLAExpressionExplanationStep {
   return { label, value, valueType: valueType(value), details, children };
}

function formatExplanationLines(step: PLAExpressionExplanationStep, depth = 0): string[] {
   const indent = "  ".repeat(depth);
   const lines = [`${indent}${step.label} = ${formatValue(step.value)} (${step.valueType})`];
   step.details?.forEach((detail) => lines.push(`${indent}  ${detail}`));
   step.children?.forEach((child) => lines.push(...formatExplanationLines(child, depth + 1)));
   return lines;
}

function explainAttributeReference(reference: string, context: PLAEvaluationContext): PLAExpressionExplanationStep {
   const attributeId = findAttributeIdByReference(reference, context);
   const definition = context.project?.elementTypes[context.element.elementType]?.attributes?.[attributeId];
   const value = resolveAttributeReference(reference, context);
   return explanationStep(`[${reference}]`, value, [
      `Element: ${elementLabel(context.element)}`,
      `Attribute id: ${attributeId}`,
      `Definition type: ${definition?.type || "unknown"}`,
      `Raw value type: ${valueType(value)}`
   ]);
}

function explainTraversalAttributeReference(attribute: AttributeReference, context: PLAEvaluationContext): { attributeName: string; step?: PLAExpressionExplanationStep } {
   if (attribute.type === "literal") return { attributeName: attribute.name };
   const step = explainAttributeReference(attribute.name, context);
   if (typeof step.value !== "string" || !step.value.trim()) throw new Error(`Cannot evaluate [${attribute.name}] as traversal attribute name: expected a non-empty string`);
   return { attributeName: step.value.trim(), step };
}

function explainAggregateValues(name: string, values: any[], label: string): any {
   return aggregateValues(name, values, label);
}

function explainTraversal(aggregator: string, relationType: string, attributeName: string, context: PLAEvaluationContext, deep = false, functionName = aggregator): PLAExpressionExplanationStep {
   const relationTypeId = findRelationTypeIdByReference(relationType, context);
   const targets = new Set<string>();
   const pending = [context.element.id];
   const visited = new Set<string>();
   while (pending.length > 0) {
      const currentId = pending.shift()!;
      if (visited.has(currentId)) continue;
      visited.add(currentId);
      context.relations
         .filter((relation) => {
            if (relationTypeId === "outgoing") return relation.fromId === currentId;
            if (relationTypeId === "incoming") return relation.toId === currentId;
            return relation.fromId === currentId && relation.relationType === relationTypeId;
         })
         .forEach((relation) => {
            const targetId = relationTypeId === "incoming" ? relation.fromId : relation.toId;
            if (targetId === context.element.id || targets.has(targetId)) return;
            if (deep) {
               targets.add(targetId);
               pending.push(targetId);
               return;
            }
            const targetElement = context.elements.find((element) => element.id === targetId);
            if (!targetElement) return;
            const result = getElementAttributeValue(targetElement, attributeName, context);
            if (result.found) {
               targets.add(targetId);
            } else if (!visited.has(targetId)) {
               pending.push(targetId);
            }
         });
   }
   const valueSteps = Array.from(targets)
      .map((targetId) => context.elements.find((element) => element.id === targetId))
      .filter(Boolean)
      .map((element) => {
         const targetElement = element as PLAElementRecord;
         const value = getElementAttributeValue(targetElement, attributeName, context).value;
         return explanationStep(`${attributeName} on ${elementLabel(targetElement)}`, value, [`Element type: ${targetElement.elementType}`]);
      });
   const values = valueSteps.map((step) => step.value);
   const result = explainAggregateValues(aggregator, values, `${functionName}(${relationType}.${attributeName})`);
   return explanationStep(`${functionName}(${relationType}.${attributeName})`, result, [
      `Start element: ${elementLabel(context.element)}`,
      `Relation type id: ${relationTypeId}`,
      `Traversal mode: ${deep ? "deep" : "stop at matching attribute"}`,
      `Visited elements: ${visited.size}`,
      `Related values: ${values.length}`
   ], valueSteps);
}

function explainParentLookup(relationType: string, attributeName: string, context: PLAEvaluationContext): PLAExpressionExplanationStep {
   if (!context.project) throw new Error("Cannot evaluate parent lookup without project definitions");
   const relationTypeId = findRelationTypeIdByReference(relationType, context);
   const pending = [context.element.id];
   const visited = new Set<string>([context.element.id]);
   const inspected: PLAExpressionExplanationStep[] = [];

   while (pending.length > 0) {
      const currentId = pending.shift()!;
      const parentRelations = context.relations.filter((relation) => relation.toId === currentId && relation.relationType === relationTypeId);
      for (const relation of parentRelations) {
         if (visited.has(relation.fromId)) continue;
         visited.add(relation.fromId);
         const parentElement = context.elements.find((element) => element.id === relation.fromId);
         if (!parentElement) throw new Error(`Cannot evaluate parent(${relationType}.${attributeName}): missing parent element ${relation.fromId}`);
         const result = getElementAttributeValue(parentElement, attributeName, context);
         inspected.push(explanationStep(`Inspect parent ${elementLabel(parentElement)}`, result.value, [`Attribute found: ${result.found}`]));
         if (result.found) return explanationStep(`parent(${relationType}.${attributeName})`, result.value, [`Relation type id: ${relationTypeId}`], inspected);
         pending.push(parentElement.id);
      }
   }

   return explanationStep(`parent(${relationType}.${attributeName})`, 0, [
      `Relation type id: ${relationTypeId}`,
      `No parent path with attribute ${attributeName} found`
   ], inspected);
}

function explainParentTraversal(aggregator: string, parentRelationType: string, relationType: string, attributeName: string, context: PLAEvaluationContext): PLAExpressionExplanationStep {
   if (!context.project) throw new Error("Cannot evaluate parent traversal without project definitions");
   const parentRelationTypeId = findRelationTypeIdByReference(parentRelationType, context);
   const relationTypeId = findRelationTypeIdByReference(relationType, context);
   const pending = [context.element.id];
   const visited = new Set<string>([context.element.id]);
   const inspected: PLAExpressionExplanationStep[] = [];

   while (pending.length > 0) {
      const currentId = pending.shift()!;
      const parentRelations = context.relations.filter((relation) => relation.toId === currentId && relation.relationType === parentRelationTypeId);
      for (const parentRelation of parentRelations) {
         if (visited.has(parentRelation.fromId)) continue;
         visited.add(parentRelation.fromId);
         const parentElement = context.elements.find((element) => element.id === parentRelation.fromId);
         if (!parentElement) throw new Error(`Cannot evaluate parent${aggregator}(${parentRelationType}.${relationType}.${attributeName}): missing parent element ${parentRelation.fromId}`);

         const targetRelations = context.relations.filter((relation) => relation.fromId === parentElement.id && relation.relationType === relationTypeId);
         if (targetRelations.length > 0) {
            const valueSteps = targetRelations.map((relation) => {
               const targetElement = context.elements.find((element) => element.id === relation.toId);
               if (!targetElement) throw new Error(`Cannot evaluate parent${aggregator}(${parentRelationType}.${relationType}.${attributeName}): missing related element ${relation.toId}`);
               const value = getElementAttributeValue(targetElement, attributeName, context).value;
               return explanationStep(`${attributeName} on ${elementLabel(targetElement)}`, value, [`Relation id: ${relation.id}`]);
            });
            const values = valueSteps.map((step) => step.value);
            const result = explainAggregateValues(aggregator, values, `parent${aggregator}(${parentRelationType}.${relationType}.${attributeName})`);
            return explanationStep(`parent${aggregator}(${parentRelationType}.${relationType}.${attributeName})`, result, [
               `Matched parent: ${elementLabel(parentElement)}`,
               `Parent relation type id: ${parentRelationTypeId}`,
               `Target relation type id: ${relationTypeId}`
            ], [...inspected, ...valueSteps]);
         }

         inspected.push(explanationStep(`Inspect parent ${elementLabel(parentElement)}`, "no related values", [`Target relation type id: ${relationTypeId}`]));
         pending.push(parentElement.id);
      }
   }

   return explanationStep(`parent${aggregator}(${parentRelationType}.${relationType}.${attributeName})`, 0, [
      `Parent relation type id: ${parentRelationTypeId}`,
      `Target relation type id: ${relationTypeId}`,
      `No parent path with relation ${relationType} found`
   ], inspected);
}

function explainNode(node: ExpressionNode, context: PLAEvaluationContext): PLAExpressionExplanationStep {
   switch (node.type) {
      case "number": return explanationStep(nodeLabel(node), node.value);
      case "attribute": return explainAttributeReference(node.name, context);
      case "binary": {
         const left = explainNode(node.left, context);
         const right = explainNode(node.right, context);
         let value: any;
         if ([">", "<", ">=", "<=", "==", "!="].includes(node.operator)) value = compare(node.operator, left.value, right.value);
         else if (node.operator === "+") value = numeric(left.value) + numeric(right.value);
         else if (node.operator === "-") value = numeric(left.value) - numeric(right.value);
         else if (node.operator === "*") value = numeric(left.value) * numeric(right.value);
         else if (node.operator === "/") value = numeric(left.value) / numeric(right.value);
         else throw new Error(`Unsupported operator '${node.operator}'`);
         return explanationStep(nodeLabel(node), value, [`Operator: ${node.operator}`], [left, right]);
      }
      case "function": {
         const args = node.args.map((arg) => explainNode(arg, context));
         const result = explainAggregateValues(node.name, args.map((arg) => numeric(arg.value)), `${node.name}(...)`);
         return explanationStep(nodeLabel(node), result, [`Function: ${node.name}`], args);
      }
      case "traversal": {
         const attribute = explainTraversalAttributeReference(node.attribute, context);
         const traversal = explainTraversal(node.aggregator, node.relationType, attribute.attributeName, context, Boolean(node.deep), node.functionName || node.aggregator);
         return attribute.step ? explanationStep(nodeLabel(node), traversal.value, [`Resolved traversal attribute: ${attribute.attributeName}`], [attribute.step, traversal]) : traversal;
      }
      case "parentLookup": {
         const attribute = explainTraversalAttributeReference(node.attribute, context);
         const lookup = explainParentLookup(node.relationType, attribute.attributeName, context);
         return attribute.step ? explanationStep(nodeLabel(node), lookup.value, [`Resolved traversal attribute: ${attribute.attributeName}`], [attribute.step, lookup]) : lookup;
      }
      case "parentTraversal": {
         const attribute = explainTraversalAttributeReference(node.attribute, context);
         const traversal = explainParentTraversal(node.aggregator, node.parentRelationType, node.relationType, attribute.attributeName, context);
         return attribute.step ? explanationStep(nodeLabel(node), traversal.value, [`Resolved traversal attribute: ${attribute.attributeName}`], [attribute.step, traversal]) : traversal;
      }
      default: throw new Error("Unsupported expression node");
   }
}

function evaluateNode(node: ExpressionNode, context: PLAEvaluationContext): any {
   switch (node.type) {
      case "number": return node.value;
      case "attribute": return resolveAttributeReference(node.name, context);
      case "binary": {
         const left = evaluateNode(node.left, context);
         const right = evaluateNode(node.right, context);
         if ([">", "<", ">=", "<=", "==", "!="].includes(node.operator)) return compare(node.operator, left, right);
         if (node.operator === "+") return numeric(left) + numeric(right);
         if (node.operator === "-") return numeric(left) - numeric(right);
         if (node.operator === "*") return numeric(left) * numeric(right);
         if (node.operator === "/") return numeric(left) / numeric(right);
         throw new Error(`Unsupported operator '${node.operator}'`);
      }
      case "function": return aggregateValues(node.name, node.args.map((arg) => numeric(evaluateNode(arg, context))), `${node.name}(...)`);
      case "traversal": return evaluateTraversal(node.aggregator, node.relationType, resolveTraversalAttributeReference(node.attribute, context), context, Boolean(node.deep), node.functionName || node.aggregator);
      case "parentLookup": return evaluateParentLookup(node.relationType, resolveTraversalAttributeReference(node.attribute, context), context);
      case "parentTraversal": return evaluateParentTraversal(node.aggregator, node.parentRelationType, node.relationType, resolveTraversalAttributeReference(node.attribute, context), context);
      default: throw new Error("Unsupported expression node");
   }
}

export function evaluatePLAExpression(expression: string, context: PLAEvaluationContext): any {
   return evaluateNode(parsePLAExpression(expression), context);
}

export function explainPLAExpression(expression: string, context: PLAEvaluationContext): PLAExpressionExplanation {
   const root = explainNode(parsePLAExpression(expression), context);
   return { expression, result: root.value, valueType: root.valueType, root, lines: formatExplanationLines(root) };
}

function aggregateValues(name: string, values: any[], label: string): any {
   if (name === "single") {
      if (values.length !== 1) throw new Error(`Cannot evaluate ${label}: expected exactly one related element but found ${values.length}`);
      return values[0];
   }
   if (name === "round") {
      if (values.length !== 1) throw new Error(`Cannot evaluate ${label}: expected exactly one argument but found ${values.length}`);
      return Math.round(numeric(values[0]));
   }
   if (values.length === 0) return 0;
   const numbers = values.map(numeric);
   switch (name) {
      case "sum": return numbers.reduce((acc, value) => acc + value, 0);
      case "avg": return numbers.reduce((acc, value) => acc + value, 0) / numbers.length;
      case "min": return Math.min(...numbers);
      case "max": return Math.max(...numbers);
      case "count": return numbers.length;
      default: throw new Error(`Unsupported function '${name}'`);
   }
}

function resolveTraversalAttributeReference(attribute: AttributeReference, context: PLAEvaluationContext): string {
   if (attribute.type === "literal") return attribute.name;
   const value = resolveAttributeReference(attribute.name, context);
   if (typeof value !== "string" || !value.trim()) throw new Error(`Cannot evaluate [${attribute.name}] as traversal attribute name: expected a non-empty string`);
   return value.trim();
}

export function evaluateTraversal(aggregator: string, relationType: string, attributeName: string, context: PLAEvaluationContext, deep = false, functionName = aggregator): any {
   const relationTypeId = findRelationTypeIdByReference(relationType, context);
   const targets = new Set<string>();
   const pending = [context.element.id];
   const visited = new Set<string>();
   while (pending.length > 0) {
      const currentId = pending.shift()!;
      if (visited.has(currentId)) continue;
      visited.add(currentId);
      context.relations
         .filter((relation) => {
            if (relationTypeId === "outgoing") return relation.fromId === currentId;
            if (relationTypeId === "incoming") return relation.toId === currentId;
            return relation.fromId === currentId && relation.relationType === relationTypeId;
         })
         .forEach((relation) => {
            const targetId = relationTypeId === "incoming" ? relation.fromId : relation.toId;
            if (targetId === context.element.id || targets.has(targetId)) return;
            if (deep) {
               targets.add(targetId);
               pending.push(targetId);
               return;
            }
            const targetElement = context.elements.find((element) => element.id === targetId);
            if (!targetElement) return;
            const result = getElementAttributeValue(targetElement, attributeName, context);
            if (result.found) {
               targets.add(targetId);
            } else if (!visited.has(targetId)) {
               pending.push(targetId);
            }
         });
   }
   const values = Array.from(targets)
      .map((targetId) => context.elements.find((element) => element.id === targetId))
      .filter(Boolean)
      .map((element) => {
         const targetElement = element as PLAElementRecord;
         return getElementAttributeValue(targetElement, attributeName, context).value;
      });
   return aggregateValues(aggregator, values, `${functionName}(${relationType}.${attributeName})`);
}

function getElementAttributeValue(element: PLAElementRecord, attributeName: string, context: PLAEvaluationContext): { found: boolean; value?: any } {
   const attributeId = findAttributeIdByReference(attributeName, context, element);
   const definitions = context.project?.elementTypes[element.elementType]?.attributes || {};
   if (definitions[attributeId]?.type === "expression") {
      if (context.resolveCalculatedAttribute) return { found: true, value: context.resolveCalculatedAttribute(element, attributeId) };
      return { found: true, value: resolvePLACalculatedAttributes(element, context.project!, context.elements, context.relations)[attributeId] };
   }
   if (Object.prototype.hasOwnProperty.call(element.attributes, attributeId)) {
      return { found: true, value: resolveAttributeValue(element.attributes[attributeId], {}, attributeId) };
   }
   return { found: false };
}

export function evaluateParentLookup(relationType: string, attributeName: string, context: PLAEvaluationContext): any {
   if (!context.project) throw new Error("Cannot evaluate parent lookup without project definitions");
   const relationTypeId = findRelationTypeIdByReference(relationType, context);
   const pending = [context.element.id];
   const visited = new Set<string>([context.element.id]);

   while (pending.length > 0) {
      const currentId = pending.shift()!;
      const parentRelations = context.relations.filter((relation) => relation.toId === currentId && relation.relationType === relationTypeId);
      for (const relation of parentRelations) {
         if (visited.has(relation.fromId)) continue;
         visited.add(relation.fromId);
         const parentElement = context.elements.find((element) => element.id === relation.fromId);
         if (!parentElement) throw new Error(`Cannot evaluate parent(${relationType}.${attributeName}): missing parent element ${relation.fromId}`);

         try {
            const result = getElementAttributeValue(parentElement, attributeName, context);
            if (result.found) return result.value;
         } catch (error: any) {
            throw new Error(`Cannot evaluate parent(${relationType}.${attributeName}) on ${parentElement.name || parentElement.id}: ${error.message || error}`);
         }
         pending.push(parentElement.id);
      }
   }

   return 0;
}

export function evaluateParentTraversal(aggregator: string, parentRelationType: string, relationType: string, attributeName: string, context: PLAEvaluationContext): any {
   if (!context.project) throw new Error("Cannot evaluate parent traversal without project definitions");
   const parentRelationTypeId = findRelationTypeIdByReference(parentRelationType, context);
   const relationTypeId = findRelationTypeIdByReference(relationType, context);
   const pending = [context.element.id];
   const visited = new Set<string>([context.element.id]);

   while (pending.length > 0) {
      const currentId = pending.shift()!;
      const parentRelations = context.relations.filter((relation) => relation.toId === currentId && relation.relationType === parentRelationTypeId);
      for (const parentRelation of parentRelations) {
         if (visited.has(parentRelation.fromId)) continue;
         visited.add(parentRelation.fromId);
         const parentElement = context.elements.find((element) => element.id === parentRelation.fromId);
         if (!parentElement) throw new Error(`Cannot evaluate parent${aggregator}(${parentRelationType}.${relationType}.${attributeName}): missing parent element ${parentRelation.fromId}`);

         const targetRelations = context.relations.filter((relation) => relation.fromId === parentElement.id && relation.relationType === relationTypeId);
         if (targetRelations.length > 0) {
            const values = targetRelations.map((relation) => {
               const targetElement = context.elements.find((element) => element.id === relation.toId);
               if (!targetElement) throw new Error(`Cannot evaluate parent${aggregator}(${parentRelationType}.${relationType}.${attributeName}): missing related element ${relation.toId}`);
               return getElementAttributeValue(targetElement, attributeName, context).value;
            });
            return aggregateValues(aggregator, values, `parent${aggregator}(${parentRelationType}.${relationType}.${attributeName})`);
         }

         pending.push(parentElement.id);
      }
   }

   return 0;
}

export function resolvePLACalculatedAttributes(element: PLAElementRecord, project: PLAProject, elements: PLAElementRecord[], relations: PLARelationRecord[], options: { includeErrors?: boolean; errors?: { attributeId: string; message: string }[] } = {}): Record<string, any> {
   const definitions = project.elementTypes[element.elementType]?.attributes || {};
   const resolved: Record<string, any> = {};
   const resolving = new Set<string>();

   const resolve = (attributeId: string): any => {
      if (Object.prototype.hasOwnProperty.call(resolved, attributeId)) return resolved[attributeId];
      if (resolving.has(attributeId)) throw new Error(`Circular calculated attribute dependency: ${attributeId}`);
      const definition = definitions[attributeId];
      if (!definition || definition.type !== "expression") return resolveAttributeValue(element.attributes[attributeId], resolved, attributeId);
      resolving.add(attributeId);
      try {
         resolved[attributeId] = evaluatePLAExpression(definition.expression || "0", {
            element,
            elements,
            relations,
            project,
            calculated: resolved,
            resolveCalculatedAttribute: (targetElement, targetAttributeId) => {
               if (targetElement.id === element.id) return resolve(targetAttributeId);
               return resolvePLACalculatedAttributes(targetElement, project, elements, relations)[targetAttributeId];
            }
         });
      } catch (error: any) {
         options.errors?.push({ attributeId, message: error.message || String(error) });
         if (!options.includeErrors) throw error;
         resolved[attributeId] = error.message || String(error);
      } finally {
         resolving.delete(attributeId);
      }
      return resolved[attributeId];
   };

   Object.keys(definitions).forEach((attributeId) => {
      if (definitions[attributeId].type === "expression") resolve(attributeId);
   });
   return resolved;
}

export function resolvePLAElementExpressionAttributes(element: PLAElementRecord, project: PLAProject, elements: PLAElementRecord[], relations: PLARelationRecord[], options: { includeErrors?: boolean } = {}): PLAElementRecord {
   const definitions = project.elementTypes[element.elementType]?.attributes || {};
   const calculated = resolvePLACalculatedAttributes(element, project, elements, relations, options);
   const attributes = { ...element.attributes };

   Object.entries(definitions).forEach(([attributeId, definition]) => {
      if (definition.type !== "expression") return;
      attributes[attributeId] = { mode: "constant", valueType: definition.valueType || "string", value: calculated[attributeId] };
   });

   return { ...element, attributes };
}

export function validatePLAData(project: PLAProject, elements: PLAElementRecord[], relations: PLARelationRecord[]): string[] {
   const errors: string[] = [];
   const ids = new Set<string>();
   elements.forEach((element) => {
      if (ids.has(element.id)) errors.push(`Duplicate element id: ${element.id}`);
      ids.add(element.id);
      if (!project.elementTypes[element.elementType]) errors.push(`Unknown element type: ${element.elementType}`);
      const definitions = project.elementTypes[element.elementType]?.attributes || {};
      Object.entries(definitions).forEach(([attributeId, definition]) => {
         if (definition.type === "expression") {
            const validation = validatePLAExpression(definition.expression || "", { project, elementTypeId: element.elementType });
            if (!validation.valid) errors.push(`${attributeId}: ${validation.error}`);
         }
         Object.entries(definition.alerts || {}).forEach(([alertId, alert]) => {
            const validation = validatePLAExpression(alert.booleanExpression, { project, elementTypeId: element.elementType });
            if (!validation.valid) errors.push(`${attributeId}.${alertId}: ${validation.error}`);
         });
      });
      try {
         resolvePLACalculatedAttributes(element, project, elements, relations);
      } catch (error: any) {
         errors.push(error.message || String(error));
      }
   });

   const relationIds = new Set<string>();
   relations.forEach((relation) => {
      if (relationIds.has(relation.id)) errors.push(`Duplicate relation id: ${relation.id}`);
      relationIds.add(relation.id);
      if (!project.relationTypes[relation.relationType]) errors.push(`Unknown relation type: ${relation.relationType}`);
      if (!ids.has(relation.fromId)) errors.push(`Missing from element: ${relation.fromId}`);
      if (!ids.has(relation.toId)) errors.push(`Missing to element: ${relation.toId}`);
   });

   const visiting = new Set<string>();
   const visited = new Set<string>();
   const outgoing = relations.reduce<Record<string, string[]>>((acc, relation) => {
      acc[relation.fromId] = [...(acc[relation.fromId] || []), relation.toId];
      return acc;
   }, {});
   const visit = (id: string): boolean => {
      if (visiting.has(id)) return true;
      if (visited.has(id)) return false;
      visiting.add(id);
      for (const target of outgoing[id] || []) {
         if (visit(target)) return true;
      }
      visiting.delete(id);
      visited.add(id);
      return false;
   };
   Array.from(ids).forEach((id) => {
      if (visit(id)) errors.push(`Circular element relation detected at ${id}`);
   });

   return errors;
}

export function getPLAAlerts(element: PLAElementRecord, project: PLAProject, elements: PLAElementRecord[], relations: PLARelationRecord[]): string[] {
   return getPLAAlertDetails(element, project, elements, relations).map((alert) => alert.message);
}

export function getPLAAlertDetails(element: PLAElementRecord, project: PLAProject, elements: PLAElementRecord[], relations: PLARelationRecord[]): { attributeId: string; message: string }[] {
   const definitions = project.elementTypes[element.elementType]?.attributes || {};
   const alerts: { attributeId: string; message: string }[] = [];
   const calculated = resolvePLACalculatedAttributes(element, project, elements, relations, { includeErrors: true, errors: alerts });
   Object.entries(definitions).forEach(([attributeId, definition]) => {
      Object.values(definition.alerts || {}).forEach((alert) => {
         try {
            if (evaluatePLAExpression(alert.booleanExpression, { element, elements, relations, project, calculated })) {
               alerts.push({ attributeId, message: alert.alertMessage });
            }
         } catch (error: any) {
            alerts.push({ attributeId, message: error.message || String(error) });
         }
      });
   });
   return alerts;
}
